"use client";

import { useEffect, useRef } from "react";

export const isVendorDebugEnabled = () => {
  if (typeof window !== "undefined") {
    try {
      return localStorage.getItem("vendor-debug") === "true";
    } catch {
      return false;
    }
  }
  return false;
};

function debugLog(vendorName: string, message: string, data?: Record<string, unknown>) {
  if (!isVendorDebugEnabled()) return;
  console.log(
    `%c[vendor-health:${vendorName}]%c ${message}`,
    "color: #0ea5e9; font-weight: bold",
    "color: inherit",
    data ?? "",
  );
}

function isLocalEnvironment() {
  if (isVendorDebugEnabled()) {
    return false;
  }
  if (typeof window !== "undefined") {
    const { hostname } = window.location;
    if (hostname === "localhost" || hostname === "127.0.0.1") return true;
  }
  return false;
}

export function useVendorHealthCheck(vendorName: string, check: () => boolean, timeoutMs: number, enabled = true) {
  const checkRef = useRef(check);
  const startTimeRef = useRef<number>(0);

  useEffect(() => {
    checkRef.current = check;
  }, [check]);

  useEffect(() => {
    if (isLocalEnvironment()) {
      debugLog(vendorName, "SKIPPED — local environment detected");
      return;
    }
    if (!enabled) {
      debugLog(vendorName, "SKIPPED — not enabled (waiting for prerequisite)", { enabled });
      return;
    }

    startTimeRef.current = performance.now();
    debugLog(vendorName, `STARTED — timer set for ${timeoutMs}ms`, {
      timeoutMs,
      url: window.location.href,
    });

    const pollInterval = isVendorDebugEnabled()
      ? setInterval(() => {
          const elapsed = Math.round(performance.now() - startTimeRef.current);
          const status = checkRef.current();
          debugLog(vendorName, `POLL @ ${elapsed}ms`, { healthy: status });
          if (status) {
            debugLog(vendorName, `HEALTHY — initialized after ~${elapsed}ms`);
            clearInterval(pollInterval);
          }
        }, 3_000)
      : undefined;

    const timer = setTimeout(() => {
      const healthy = checkRef.current();
      const elapsed = Math.round(performance.now() - startTimeRef.current);

      if (!healthy) {
        debugLog(vendorName, `FAILED — not initialized after ${elapsed}ms`, {
          timeoutMs,
          url: window.location.href,
        });
        if (window.Sentry?.captureException) {
          window.Sentry.captureException(
            new Error(`[vendor-health] ${vendorName} failed to initialize within ${timeoutMs}ms`),
            {
              extra: {
                vendorName,
                timeoutMs,
                url: `${window.location.origin}${window.location.pathname}`,
                userAgent: navigator.userAgent,
                timestamp: new Date().toISOString(),
              },
            },
          );
        }
      } else {
        debugLog(vendorName, `PASSED — healthy at timeout check (${elapsed}ms)`);
      }

      if (pollInterval) clearInterval(pollInterval);
    }, timeoutMs);

    return () => {
      clearTimeout(timer);
      if (pollInterval) clearInterval(pollInterval);
      debugLog(vendorName, "CLEANUP — timer cleared (unmount or deps change)");
    };
  }, [vendorName, timeoutMs, enabled]);
}
